import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, TextField, SelectField, IconButton, Tooltip, Spinner } from '@dopomogai/ui';
import { Globe, Monitor, Tablet, Smartphone, RotateCcw, Maximize2, Minimize2, RefreshCw } from 'lucide-react';

type DeviceKind = 'desktop' | 'tablet' | 'mobile';

const DEVICE_PRESETS = {
  desktop: { w: 1280, h: 800, label: 'Desktop' },
  tablet: { w: 834, h: 1112, label: 'Tablet' },
  mobile: { w: 375, h: 812, label: 'Mobile' },
};

export interface SitePreviewTabContentProps {
  platformApiBase: string;
  previewToken: string | null;
  allowedOrigins: string[];
}

export const SitePreviewTabContent: React.FC<SitePreviewTabContentProps> = ({
  platformApiBase,
  previewToken,
  allowedOrigins,
}) => {
  const [origin, setOrigin] = useState<string>(allowedOrigins[0] || '');
  const [path, setPath] = useState<string>('/');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [device, setDevice] = useState<DeviceKind>('desktop');
  const [rotated, setRotated] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!origin && allowedOrigins.length > 0) setOrigin(allowedOrigins[0]);
  }, [allowedOrigins, origin]);

  const deviceSize = useMemo(() => {
    const base = DEVICE_PRESETS[device];
    const w = rotated ? base.h : base.w;
    const h = rotated ? base.w : base.h;
    return { w, h, label: base.label };
  }, [device, rotated]);

  const toggleFullscreen = useCallback(async () => {
    try {
      const el = wrapperRef.current;
      if (!el) return;
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {}
  }, []);

  const buildTargetUrl = () => {
    const host = origin?.trim();
    let p = path?.trim() || '/';
    if (!p.startsWith('/')) p = '/' + p;
    const protocol = host.includes('localhost') ? 'http' : 'https';
    return `${protocol}://${host}${p}`;
  };

  const loadPreview = useCallback(async () => {
    if (!previewToken) {
      setError('Preview token is not ready yet. Save a draft or wait a moment.');
      return;
    }
    if (!origin) {
      setError('Select an origin from allowed origins.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${platformApiBase.replace(/\/$/, '')}/api/v1/widgets/preview/render`);
      url.searchParams.set('target_url', buildTargetUrl());
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${previewToken}` },
        credentials: 'include',
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`Preview render failed (${res.status}). ${msg?.slice(0, 120)}`);
      }
      const html = await res.text();
      if (iframeRef.current) {
        iframeRef.current.srcdoc = html;
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load site preview.');
    } finally {
      setLoading(false);
    }
  }, [platformApiBase, previewToken, origin, path]);

  // Keep iframe clean when inputs change (optional)
  useEffect(() => {
    if (iframeRef.current) iframeRef.current.srcdoc = '<!doctype html><html><head><meta charset="utf-8"/></head><body></body></html>';
  }, [origin]);

  const originOptions = useMemo(
    () => (allowedOrigins || []).map(o => ({ value: o, label: o })),
    [allowedOrigins]
  );

  return (
    <div className="space-y-6">
      <Card title="Site Preview" icon={<Globe className="w-5 h-5" />} noPadding={false} headerBgColor="bg-slate-100">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <SelectField
            label="Allowed origin"
            name="origin"
            value={origin || ''}
            options={originOptions}
            onChange={(val) => setOrigin(String(val))}
            placeholder={originOptions.length ? 'Pick a domain' : 'No allowed origins'}
          />
          <TextField
            label="Path"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/, /pricing, /docs/faq"
          />
          <div className="flex items-end">
            <Button
              variant="secondary"
              icon={<RefreshCw size={16} />}
              text={loading ? 'Loading…' : 'Load'}
              onClick={loadPreview}
              disabled={loading || !origin || !previewToken}
            />
          </div>
        </div>
        {!!error && <div className="text-sm text-rose-600 mt-2">{error}</div>}
        {!error && loading && (
          <div className="mt-2">
            <Spinner text="Rendering site preview…" />
          </div>
        )}
      </Card>

      <Card noPadding className="flex-1 min-h-0">
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
          <div className="text-sm text-slate-600">
            Target: {origin ? `${origin}${path?.startsWith('/') ? path : `/${path}`}` : '—'}
          </div>
          <div className="flex items-center gap-2">
            <Tooltip text="Desktop">
              <IconButton ariaLabel="Desktop preview" icon={<Monitor className={device === 'desktop' ? 'text-slate-900' : 'text-slate-500'} size={16} />} variant="ghost" onClick={() => setDevice('desktop')} />
            </Tooltip>
            <Tooltip text="Tablet">
              <IconButton ariaLabel="Tablet preview" icon={<Tablet className={device === 'tablet' ? 'text-slate-900' : 'text-slate-500'} size={16} />} variant="ghost" onClick={() => setDevice('tablet')} />
            </Tooltip>
            <Tooltip text="Mobile">
              <IconButton ariaLabel="Mobile preview" icon={<Smartphone className={device === 'mobile' ? 'text-slate-900' : 'text-slate-500'} size={16} />} variant="ghost" onClick={() => setDevice('mobile')} />
            </Tooltip>
            <Tooltip text="Rotate">
              <IconButton ariaLabel="Rotate" icon={<RotateCcw size={16} />} variant="ghost" onClick={() => setRotated(r => !r)} disabled={device === 'desktop'} />
            </Tooltip>
            <Tooltip text={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton ariaLabel="Fullscreen" icon={isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />} variant="ghost" onClick={toggleFullscreen} />
            </Tooltip>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-slate-50">
          <div ref={wrapperRef} className="h-full w-full flex items-center justify-center p-4">
            <div style={{ width: deviceSize.w, height: deviceSize.h }} className="bg-white border border-slate-300 rounded-xl overflow-hidden shadow-sm">
              <iframe ref={iframeRef} title="Site preview" className="w-full h-full border-0" sandbox="allow-scripts allow-same-origin allow-forms" />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};